import { Fragment, FunctionComponent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { trackFeature } from '@/utils/adobeLaunch';
import { t } from '@/utils/i18n';
import { hasUserAPI, isCYOMode } from '@/utils/params';
import { useFullScreen } from '@/hooks/useFullScreen';
import { getFeatures } from '@/redux/core/getters/getFeatures';
import { getUserId } from '@/redux/core/getters/getUserId';
import { hasCopyrightErrors } from '@/redux/draft/getters/hasCopyrightErrors';
import { isDraftValid } from '@/redux/draft/getters/isDraftValid';
import { StoreDispatch, StoreState } from '@/redux/types';
import { getUserDraftsAction } from '@/redux/userDrafts/actions/getUserDraftsAction';
import { getDraftCount } from '@/redux/userDrafts/getters/getDraftCount';
import { Design } from '@/components/common/icons/Design';
import { Phone } from '@/components/common/icons/Phone';
import { Save } from '@/components/common/icons/Save';
import { Share } from '@/components/common/icons/Share';
import { ShirtPile } from '@/components/common/icons/ShirtPile';
import { SHARE_ROUTE } from '@/components/common/sidebar/sharing/Sharing';
import { LIST_ROUTE, SAVE_ROUTE } from '@/components/common/user-drafts/AsyncUserDraftsDialog';
import './DesignerHeader.scss';

const headerClass = 'header-button';
const headerClassDisabled = `${headerClass} ${headerClass}--disabled`;

interface Props {
  disabled: boolean;
}

const ShareButton: FunctionComponent<Props> = ({ disabled }) => {
  const saveAndShareEnabled =
    useSelector<StoreState, boolean>((s) => isCYOMode() || isDraftValid(s)) && !disabled;
  const content = (
    <Fragment>
      <Share className="header-button__icon" />
      <span className="header-button__label">{t('share.title')}</span>
    </Fragment>
  );

  if (saveAndShareEnabled) {
    return (
      <Link
        to={SHARE_ROUTE}
        onClick={() => trackFeature('menu-save-share')}
        className={headerClass}
      >
        {content}
      </Link>
    );
  }

  return <div className={headerClassDisabled}>{content}</div>;
};

export const DesignerHeader: FunctionComponent = () => {
  const { showSaveAndShare, showUserDrafts } = useSelector(getFeatures);
  const hasCopyrightError = useSelector(hasCopyrightErrors);
  const [toggleFullscreen, FullscreenIcon] = useFullScreen();

  const trackBulkClick = () => {
    trackFeature('menu-bulk');
    const date = new Date();
    date.setTime(date.getTime() + 24 * 60 * 60 * 1000);
    document.cookie = `bulk_b=1;expires=${date.toUTCString()};path=/`;
  };

  const showCYOHeaderButtons = isCYOMode();

  const draftCount = useSelector(getDraftCount);
  const userId = useSelector(getUserId);
  const storeDispatch = useDispatch<StoreDispatch>();

  useEffect(() => {
    if (isCYOMode() && showUserDrafts && userId) {
      storeDispatch(getUserDraftsAction(userId));
    }
  }, [userId, storeDispatch, showUserDrafts]);

  return (
    <div className="designer-header">
      {showCYOHeaderButtons && (
        <div className="header-button" role="menuitem" tabIndex={0}>
          <Phone className="header-button__icon" />
          <span className="header-button__label">{t('contact.label')}</span>
          <div className="header-button__tooltip header-tooltip">
            <div className="header-tooltip__arrow" />
            <a className="header-tooltip__link" href={t('contact.contactForm.href')} target="blank">
              {t('contact.contactForm')}
            </a>
            <div className="header-tooltip__divider" />
            <div className="header-tooltip__bulk">
              <ShirtPile width="1.5em" height="1.5em" />
              <div>
                <div>{t('bulk.label')}</div>
                <div>{t('bulk.headline')}</div>
              </div>
            </div>
            <a
              className="header-tooltip__link"
              href={t('bulk.linkHref')}
              target="blank"
              onClick={trackBulkClick}
            >
              {t('bulk.linkText')}
            </a>
          </div>
        </div>
      )}
      {showCYOHeaderButtons && showSaveAndShare && hasUserAPI() && showUserDrafts && (
        <div
          className={`header-button${hasCopyrightError ? ' header-button--disabled' : ''}`}
          role="menuitem"
          tabIndex={0}
        >
          <Save className="header-button__icon" />
          <span className="header-button__label">{t('draft.nav.title')}</span>
          <div className="header-button__tooltip header-tooltip">
            <div className="header-tooltip__arrow" />
            <div className="header-tooltip__content">
              <div className="header-tooltip__line">
                <Save width="1.25rem" height="1.25rem" />
                <Link
                  to={SAVE_ROUTE}
                  className="header-tooltip__item"
                  onClick={() => trackFeature('menu-save-draft')}
                >
                  {t('draft.nav.save')}
                </Link>
              </div>
              <div className="header-tooltip__line">
                <Design width="1.25rem" height="1.25rem" />
                <Link
                  to={LIST_ROUTE}
                  className="header-tooltip__item"
                  onClick={() => trackFeature('menu-view-drafts')}
                >
                  {t('draft.nav.view')}
                </Link>
                {draftCount > 0 && <span className="header-tooltip__count">{draftCount}</span>}
              </div>
            </div>
          </div>
          {draftCount > 0 && <span className="header-button__count">{draftCount}</span>}
        </div>
      )}
      {showSaveAndShare && <ShareButton disabled={hasCopyrightError} />}
      {toggleFullscreen && (
        <button onClick={toggleFullscreen} className="header-button header-button--fullscreen">
          <FullscreenIcon className="header-button__icon" />
          <span className="header-button__label">{t('fullscreen.title')}</span>
        </button>
      )}
    </div>
  );
};
